import { createSlice } from '@reduxjs/toolkit';

import {
    completeInterviewReview as completeInterviewReviewAPI,
    createResubmissionGuidance as createResubmissionGuidanceAPI,
    createInterviewQuestion as createInterviewQuestionAPI,
    deleteInterviewQuestion as deleteInterviewQuestionAPI,
    deleteResubmissionGuidance as deleteResubmissionGuidanceAPI,
    deleteEvidenceFile as deleteEvidenceFileAPI,
    createEvidenceLibraryUploadUrl as createEvidenceLibraryUploadUrlAPI,
    uploadEvidenceToS3 as uploadEvidenceToS3API,
    completeEvidenceUpload as completeEvidenceUploadAPI,
    getCandidateAssessment as getCandidateAssessmentAPI,
    getCandidateAssessmentInfo as getCandidateAssessmentInfoAPI,
    getCandidateAssessmentOptions as getCandidateAssessmentOptionsAPI,
    getCandidateAssessments as getCandidateAssessmentsAPI,
    generateAssessmentReports as generateAssessmentReportsAPI,
    generateRplAuditReport as generateRplAuditReportAPI,
    getInterviewRequirements as getInterviewRequirementsAPI,
    getResubmissionGuidance as getResubmissionGuidanceAPI,
    getEvidenceAccessUrl as getEvidenceAccessUrlAPI,
    getEvidenceFileAccessUrl as getEvidenceFileAccessUrlAPI,
    getEvidenceLibrary as getEvidenceLibraryAPI,
    inviteCandidate as inviteCandidateAPI,
    reorderInterviewQuestions as reorderInterviewQuestionsAPI,
    submitResubmissionGuidance as submitResubmissionGuidanceAPI,
    updateResubmissionGuidance as updateResubmissionGuidanceAPI,
    updateInterviewQuestion as updateInterviewQuestionAPI,
    updateInterviewMappingReview as updateInterviewMappingReviewAPI,
	    reconcileInterviewScript as reconcileInterviewScriptAPI,
	    retryInterviewScriptEmail as retryInterviewScriptEmailAPI,
    generateResubmissionGuidance as generateResubmissionGuidanceAPI,
    generateInterviewQuestions as generateInterviewQuestionsAPI,
    editAssessmentClosureStatus as editAssessmentClosureStatusAPI,
    getAppealDetails as getAppealDetailsAPI,
    appealDetailsApproval as appealDetailsApprovalAPI
} from 'api/candidate-assessments';
import {
    deleteCandidateEvidenceLibraryFile as deleteCandidateEvidenceLibraryFileAPI,
    getCandidateEvidenceLibrary as getCandidateEvidenceLibraryAPI,
    getCandidateEvidenceLibraryAccessUrl as getCandidateEvidenceLibraryAccessUrlAPI
} from 'api/candidates';
import { upsertProcess } from './backend-process';

const initialState = {
    list: [],
    detail: null,
    options: {
        courses: [],
        assessors: [],
        trialInviteCapacity: {
            isTrial: false,
            usedSlots: 0,
            totalSlots: 0,
            remainingSlots: null,
            limitReached: false
        }
    },
    evidenceManagement: {
        assessment: null,
        files: [],
        scope: 'assessment',
        loading: false,
        error: '',
        deleting: false,
        uploading: false,
        access: null,
        accessLoading: false
    },
    interviewRequirements: {
        list: [],
        pagination: {
            total: 0,
            totalItems: 0,
            pageNumber: 1,
            pageSize: 10
        },
        loading: false,
        error: ''
    },
    resubmissionGuidance: {
        list: [],
        pagination: {
            total: 0,
            totalItems: 0,
            pageNumber: 1,
            pageSize: 10
        },
        loading: false,
        error: ''
    },
    loading: false,
    detailLoading: false,
    optionsLoading: false,
    submitting: false,
    error: '',
    detailError: '',
    optionsError: '',
    total: {
        all: 0,
        items: 0
    },
    unit: null, // Identify current unit selected
    outcome: null, // Identify current outcome selected
    guest: null // Identify current guest selected
};

const slice = createSlice({
    name: 'candidateAssessment',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setDetailLoading(state, action) {
            state.detailLoading = action.payload;
        },
        setOptionsLoading(state, action) {
            state.optionsLoading = action.payload;
        },
        setSubmitting(state, action) {
            state.submitting = action.payload;
        },
        setError(state, action) {
            state.error = action.payload || '';
        },
        setDetailError(state, action) {
            state.detailError = action.payload || '';
        },
        setOptionsError(state, action) {
            state.optionsError = action.payload || '';
        },
        setAssessments(state, action) {
            state.list = action.payload?.data || [];
            state.total.all = action.payload?.pagination?.total || 0;
            state.total.items = action.payload?.pagination?.totalItems || 0;
        },
        setAssessmentDetail(state, action) {
            state.detail = action.payload?.data || null;
        },
        setOptions(state, action) {
            state.options = action.payload?.data || {
                courses: [],
                assessors: [],
                trialInviteCapacity: {
                    isTrial: false,
                    usedSlots: 0,
                    totalSlots: 0,
                    remainingSlots: null,
                    limitReached: false
                }
            };
        },
        setEvidenceManagementLoading(state, action) {
            state.evidenceManagement.loading = action.payload;
        },
        setEvidenceManagementError(state, action) {
            state.evidenceManagement.error = action.payload || '';
        },
        setEvidenceManagementData(state, action) {
            const { assessment, library } = action.payload || {};
            state.evidenceManagement.assessment = assessment?.data || assessment || null;
            state.evidenceManagement.files = library?.data?.files || [];
            state.evidenceManagement.scope = library?.data?.scope || 'assessment';
        },
        setEvidenceManagementAssessment(state, action) {
            state.evidenceManagement.assessment = action.payload?.data || action.payload || null;
        },
        setEvidenceManagementDeleting(state, action) {
            state.evidenceManagement.deleting = action.payload;
        },
        setEvidenceManagementUploading(state, action) {
            state.evidenceManagement.uploading = action.payload;
        },
        setEvidenceManagementAccessLoading(state, action) {
            state.evidenceManagement.accessLoading = action.payload;
        },
        setEvidenceManagementAccess(state, action) {
            state.evidenceManagement.access = action.payload?.data || action.payload || null;
        },
        setInterviewRequirementsLoading(state, action) {
            state.interviewRequirements.loading = action.payload;
        },
        setInterviewRequirementsError(state, action) {
            state.interviewRequirements.error = action.payload || '';
        },
        setInterviewRequirements(state, action) {
            state.interviewRequirements.list = action.payload?.data || [];
            state.interviewRequirements.pagination = {
                total: action.payload?.pagination?.total || 0,
                totalItems: action.payload?.pagination?.totalItems || 0,
                pageNumber: action.payload?.pagination?.pageNumber || 1,
                pageSize: action.payload?.pagination?.pageSize || 10
            };
        },
        setResubmissionGuidanceLoading(state, action) {
            state.resubmissionGuidance.loading = action.payload;
        },
        setResubmissionGuidanceError(state, action) {
            state.resubmissionGuidance.error = action.payload || '';
        },
        setResubmissionGuidance(state, action) {
            state.resubmissionGuidance.list = action.payload?.data || [];
            state.resubmissionGuidance.pagination = {
                total: action.payload?.pagination?.total || 0,
                totalItems: action.payload?.pagination?.totalItems || 0,
                pageNumber: action.payload?.pagination?.pageNumber || 1,
                pageSize: action.payload?.pagination?.pageSize || 10
            };
        },
        clearEvidenceManagement(state) {
            state.evidenceManagement = {
                assessment: null,
                files: [],
                scope: 'assessment',
                loading: false,
                error: '',
                deleting: false,
                uploading: false,
                access: null,
                accessLoading: false
            };
        },
        clearEvidenceManagementError(state) {
            state.evidenceManagement.error = '';
        },
        clearEvidenceManagementAccess(state) {
            state.evidenceManagement.access = null;
        },
        clearAssessmentDetail(state) {
            state.detail = null;
            state.detailError = '';
            state.unit = null;
            state.outcome = null;
            state.interviewRequirements = {
                list: [],
                pagination: {
                    total: 0,
                    totalItems: 0,
                    pageNumber: 1,
                    pageSize: 10
                },
                loading: false,
                error: ''
            };
            state.resubmissionGuidance = {
                list: [],
                pagination: {
                    total: 0,
                    totalItems: 0,
                    pageNumber: 1,
                    pageSize: 10
                },
                loading: false,
                error: ''
            };
        },
        setUnit(state, action) {
            state.unit = action.payload || null;
        },
        setOutcome(state, action) {
            state.outcome = action.payload || null;
        },
        setGuest(state, action) {
            state.guest = action.payload || null;
        }
    }
});

export const { clearEvidenceManagement, clearEvidenceManagementAccess, clearEvidenceManagementError, setUnit, setOutcome, setGuest } =
    slice.actions;

export default slice.reducer;

export function fetchCandidateAssessments(params = {}) {
    return async (dispatch) => {
        dispatch(slice.actions.setLoading(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await getCandidateAssessmentsAPI(params);
            dispatch(slice.actions.setAssessments(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to load candidate assessments.'));
            throw err;
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function fetchCandidateAssessment(assessmentCode, hasLoading = true) {
    return async (dispatch) => {
        if (hasLoading) dispatch(slice.actions.setDetailLoading(true));
        dispatch(slice.actions.setDetailError(''));

        try {
            const createLogs = hasLoading ? true : false;
            const response = await getCandidateAssessmentAPI(assessmentCode, createLogs);
            dispatch(slice.actions.setAssessmentDetail(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setDetailError(err?.message || 'Unable to load assessment.'));
            throw err;
        } finally {
            if (hasLoading) dispatch(slice.actions.setDetailLoading(false));
        }
    };
}

export function generateAssessmentReports(assessmentCode) {
    return async (dispatch) => {
        if (!assessmentCode) return null;

        dispatch(slice.actions.setError(''));

        try {
            const response = await generateAssessmentReportsAPI(assessmentCode);
            dispatch(slice.actions.setAssessmentDetail(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to generate assessment reports.'));
            throw err;
        }
    };
}

export function generateRplAuditReport(assessmentCode) {
    return async (dispatch) => {
        if (!assessmentCode) return null;

        dispatch(slice.actions.setError(''));

        try {
            const response = await generateRplAuditReportAPI(assessmentCode);
            dispatch(slice.actions.setAssessmentDetail(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to generate the RPL audit report.'));
            throw err;
        }
    };
}

export function fetchCandidateAssessmentOptions() {
    return async (dispatch) => {
        dispatch(slice.actions.setOptionsLoading(true));
        dispatch(slice.actions.setOptionsError(''));

        try {
            const response = await getCandidateAssessmentOptionsAPI();
            dispatch(slice.actions.setOptions(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setOptionsError(err?.message || 'Unable to load invite options.'));
            throw err;
        } finally {
            dispatch(slice.actions.setOptionsLoading(false));
        }
    };
}

export function fetchAssessmentInterviewRequirements(assessmentCode, params = {}) {
    return async (dispatch) => {
        if (!assessmentCode) return null;

        dispatch(slice.actions.setInterviewRequirementsLoading(true));
        dispatch(slice.actions.setInterviewRequirementsError(''));

        try {
            const response = await getInterviewRequirementsAPI(assessmentCode, params);
            dispatch(slice.actions.setInterviewRequirements(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setInterviewRequirementsError(err?.message || 'Unable to load interview requirements.'));
            throw err;
        } finally {
            dispatch(slice.actions.setInterviewRequirementsLoading(false));
        }
    };
}

export function fetchAssessmentResubmissionGuidance(assessmentCode, params = {}) {
    return async (dispatch) => {
        if (!assessmentCode) return null;

        dispatch(slice.actions.setResubmissionGuidanceLoading(true));
        dispatch(slice.actions.setResubmissionGuidanceError(''));

        try {
            const response = await getResubmissionGuidanceAPI(assessmentCode, params);
            dispatch(slice.actions.setResubmissionGuidance(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setResubmissionGuidanceError(err?.message || 'Unable to load resubmission guidance.'));
            throw err;
        } finally {
            dispatch(slice.actions.setResubmissionGuidanceLoading(false));
        }
    };
}

export function createAssessmentResubmissionGuidance(assessmentCode, data, params = {}) {
    return async (dispatch) => {
        if (!assessmentCode) return null;

        dispatch(slice.actions.setSubmitting(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await createResubmissionGuidanceAPI(assessmentCode, data);
            await dispatch(fetchAssessmentResubmissionGuidance(assessmentCode, params));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to create resubmission guidance.'));
            throw err;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function updateAssessmentResubmissionGuidance(assessmentCode, guidanceGroupId, data, params = {}) {
    return async (dispatch) => {
        if (!assessmentCode || !guidanceGroupId) return null;

        dispatch(slice.actions.setSubmitting(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await updateResubmissionGuidanceAPI(assessmentCode, guidanceGroupId, data);
            await dispatch(fetchAssessmentResubmissionGuidance(assessmentCode, params));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to update resubmission guidance.'));
            throw err;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function deleteAssessmentResubmissionGuidance(assessmentCode, guidanceGroupId, params = {}) {
    return async (dispatch) => {
        if (!assessmentCode || !guidanceGroupId) return null;

        dispatch(slice.actions.setSubmitting(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await deleteResubmissionGuidanceAPI(assessmentCode, guidanceGroupId);
            await dispatch(fetchAssessmentResubmissionGuidance(assessmentCode, params));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to delete resubmission guidance.'));
            throw err;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function submitAssessmentResubmissionGuidance(assessmentCode) {
    return async (dispatch) => {
        if (!assessmentCode) return null;

        dispatch(slice.actions.setSubmitting(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await submitResubmissionGuidanceAPI(assessmentCode);
            dispatch(slice.actions.setAssessmentDetail(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to submit resubmission guidance.'));
            throw err;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function completeAssessmentInterviewReview(assessmentCode, data = {}) {
    return async (dispatch) => {
        if (!assessmentCode) return null;

        dispatch(slice.actions.setSubmitting(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await completeInterviewReviewAPI(assessmentCode, data);
            if (!response?.data?.requiresInterviewRelianceConfirmation && !response?.data?.stayedInReview) {
                dispatch(slice.actions.setAssessmentDetail(response));
            }
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to complete interview review.'));
            throw err;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function createCandidateInvite(data) {
    return async (dispatch) => {
        dispatch(slice.actions.setSubmitting(true));

        try {
            return await inviteCandidateAPI(data);
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function createAssessmentInterviewQuestion(assessmentCode, data) {
    return async (dispatch) => {
        if (!assessmentCode) return null;

        dispatch(slice.actions.setSubmitting(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await createInterviewQuestionAPI(assessmentCode, data);
            dispatch(slice.actions.setAssessmentDetail(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to create interview question.'));
            throw err;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function updateAssessmentInterviewQuestion(assessmentCode, questionId, data) {
    return async (dispatch) => {
        if (!assessmentCode || !questionId) return null;

        dispatch(slice.actions.setSubmitting(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await updateInterviewQuestionAPI(assessmentCode, questionId, data);
            dispatch(slice.actions.setAssessmentDetail(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to update interview question.'));
            throw err;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function deleteAssessmentInterviewQuestion(assessmentCode, questionId) {
    return async (dispatch) => {
        if (!assessmentCode || !questionId) return null;

        dispatch(slice.actions.setSubmitting(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await deleteInterviewQuestionAPI(assessmentCode, questionId);
            dispatch(slice.actions.setAssessmentDetail(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to delete interview question.'));
            throw err;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function reorderAssessmentInterviewQuestions(assessmentCode, questionIds) {
    return async (dispatch) => {
        if (!assessmentCode || !questionIds?.length) return null;

        dispatch(slice.actions.setSubmitting(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await reorderInterviewQuestionsAPI(assessmentCode, { questionIds });
            dispatch(slice.actions.setAssessmentDetail(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to reorder interview questions.'));
            throw err;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function updateAssessmentInterviewMappingReview(assessmentCode, mappingId, data) {
    return async (dispatch) => {
        if (!assessmentCode || !mappingId) return null;

        dispatch(slice.actions.setSubmitting(true));
        dispatch(slice.actions.setError(''));

        try {
            const response = await updateInterviewMappingReviewAPI(assessmentCode, mappingId, data);
            dispatch(slice.actions.setAssessmentDetail(response));
            return response;
        } catch (err) {
            dispatch(slice.actions.setError(err?.message || 'Unable to save interview mapping review.'));
            throw err;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function reconcileAssessmentInterviewScript(assessmentCode, data) {
    return async (dispatch) => {
        dispatch(slice.actions.setSubmitting(true));
        try {
            const response = await reconcileInterviewScriptAPI(assessmentCode, data);
            dispatch(slice.actions.setAssessmentDetail(response));
            return response;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function retryAssessmentInterviewScriptEmail(assessmentCode) {
    return async (dispatch) => {
        dispatch(slice.actions.setSubmitting(true));
        try {
            const response = await retryInterviewScriptEmailAPI(assessmentCode);
            dispatch(slice.actions.setAssessmentDetail(response));
            return response;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function fetchEvidenceManagement(assessmentCode, { readOnly = false, candidateId = '', status = 'active' } = {}) {
    return async (dispatch) => {
        if (!assessmentCode && !candidateId) return null;

        dispatch(slice.actions.setEvidenceManagementLoading(true));
        dispatch(slice.actions.setEvidenceManagementError(''));

        try {
            const [assessment, library] = await Promise.all(
                readOnly && candidateId
                    ? [Promise.resolve(null), getCandidateEvidenceLibraryAPI(candidateId, { status })]
                    : readOnly
                      ? [getCandidateAssessmentAPI(assessmentCode, false), getEvidenceLibraryAPI(assessmentCode)]
                      : [getCandidateAssessmentInfoAPI(assessmentCode), getEvidenceLibraryAPI(assessmentCode)]
            );
            dispatch(slice.actions.setEvidenceManagementData({ assessment, library }));
            return { assessment, library };
        } catch (err) {
            dispatch(slice.actions.setEvidenceManagementData({ assessment: null, library: null }));
            dispatch(slice.actions.setEvidenceManagementError(err?.message || 'Unable to load evidence management.'));
            throw err;
        } finally {
            dispatch(slice.actions.setEvidenceManagementLoading(false));
        }
    };
}

/**
 * Upload a standalone library file, then refresh reusable evidence and assessment state.
 * @param {string} assessmentCode - Candidate assessment code
 * @param {File} file - File selected from the candidate's device
 * @returns {Function} Redux thunk resolving after the library is refreshed
 */
export function uploadEvidenceManagementFile(assessmentCode, uploadItem) {
    return async (dispatch) => {
        const file = uploadItem?.file || uploadItem;
        const evidenceMetadata = uploadItem?.evidenceMetadata || file?.evidenceMetadata;
        if (!assessmentCode || !file) return null;
        dispatch(slice.actions.setEvidenceManagementUploading(true));
        dispatch(slice.actions.setEvidenceManagementError(''));

        try {
            const uploadResponse = await createEvidenceLibraryUploadUrlAPI(assessmentCode, {
                originalFilename: file.name,
                fileType: file.type,
                fileSize: file.size,
                evidenceMetadata
            });
            const { uploadUrl, headers, evidence } = uploadResponse.data;
            await uploadEvidenceToS3API({ uploadUrl, headers, file });
            const response = await completeEvidenceUploadAPI(assessmentCode, evidence.evidenceId);
            await dispatch(fetchEvidenceManagement(assessmentCode));
            return response;
        } catch (err) {
            dispatch(slice.actions.setEvidenceManagementError(err?.message || 'Unable to upload evidence library file.'));
            throw err;
        } finally {
            dispatch(slice.actions.setEvidenceManagementUploading(false));
        }
    };
}

export function deleteEvidenceManagementFile(assessmentCode, evidenceId) {
    return async (dispatch) => {
        if (!assessmentCode || !evidenceId) return null;

        dispatch(slice.actions.setEvidenceManagementDeleting(true));
        dispatch(slice.actions.setEvidenceManagementError(''));

        try {
            const response = await deleteEvidenceFileAPI(assessmentCode, evidenceId);
            const library = await getEvidenceLibraryAPI(assessmentCode);
            dispatch(slice.actions.setEvidenceManagementData({ assessment: response, library }));
            return response;
        } catch (err) {
            dispatch(slice.actions.setEvidenceManagementError(err?.message || 'Unable to delete evidence file.'));
            throw err;
        } finally {
            dispatch(slice.actions.setEvidenceManagementDeleting(false));
        }
    };
}

export function deleteCandidateEvidenceManagementFile(candidateId, evidence, { status = 'active' } = {}) {
    return async (dispatch) => {
        if (!candidateId || !evidence?.evidenceId || !evidence?.recordAssessmentCode) return null;

        dispatch(slice.actions.setEvidenceManagementDeleting(true));
        dispatch(slice.actions.setEvidenceManagementError(''));

        try {
            const response = await deleteCandidateEvidenceLibraryFileAPI(
                candidateId,
                evidence.recordAssessmentCode,
                evidence.evidenceId
            );
            const library = await getCandidateEvidenceLibraryAPI(candidateId, { status });
            dispatch(slice.actions.setEvidenceManagementData({ assessment: null, library }));
            return response;
        } catch (err) {
            dispatch(slice.actions.setEvidenceManagementError(err?.message || 'Unable to delete evidence file.'));
            throw err;
        } finally {
            dispatch(slice.actions.setEvidenceManagementDeleting(false));
        }
    };
}

export function createEvidenceManagementAccess(assessmentCode, evidence, mode = 'preview', { readOnly = false, candidateId = '' } = {}) {
    return async (dispatch) => {
        if (!assessmentCode || !evidence?.evidenceId) return null;

        dispatch(slice.actions.setEvidenceManagementAccessLoading(true));
        dispatch(slice.actions.setEvidenceManagementError(''));

        try {
            const response =
                readOnly && candidateId
                    ? await getCandidateEvidenceLibraryAccessUrlAPI(candidateId, assessmentCode, evidence.evidenceId, mode)
                    : await (readOnly ? getEvidenceFileAccessUrlAPI : getEvidenceAccessUrlAPI)(assessmentCode, evidence.evidenceId, mode, {
                          sourceAssessmentId: evidence.sourceAssessmentId,
                          sourceAssessmentCode: evidence.sourceAssessmentCode
                      });
            if (response.data?.mode === 'preview') {
                dispatch(slice.actions.setEvidenceManagementAccess(response));
            } else {
                dispatch(slice.actions.clearEvidenceManagementAccess());
            }
            return response.data;
        } catch (err) {
            dispatch(slice.actions.setEvidenceManagementError(err?.message || 'Unable to open evidence.'));
            throw err;
        } finally {
            dispatch(slice.actions.setEvidenceManagementAccessLoading(false));
        }
    };
}

export function generateResubmissionGuidance(data) {
    return async (dispatch) => {
        dispatch(slice.actions.setSubmitting(true));

        try {
            const response = await generateResubmissionGuidanceAPI(data);
            if (response?.data?._id) {
                dispatch(upsertProcess(response.data));
            }
            return response;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function generateInterviewQuestions(data) {
    return async (dispatch) => {
        dispatch(slice.actions.setSubmitting(true));

        try {
            const response = await generateInterviewQuestionsAPI(data);
            if (response?.data?._id) {
                dispatch(upsertProcess(response.data));
            }
            return response;
        } finally {
            dispatch(slice.actions.setSubmitting(false));
        }
    };
}

export function editAssessmentClosureStatus(assessmentCode, params = {}) {
    return async (dispatch) => {
        try {
            const response = await editAssessmentClosureStatusAPI(assessmentCode, params);
            return response;
        } catch (err) {
            throw err;
        }
    };
}

export function getAppealDetails(assessmentCode) {
    return async (dispatch) => {
        try {
            const response = await getAppealDetailsAPI(assessmentCode);
            return response;
        } catch (err) {
            throw err;
        }
    };
}

export function appealDetailsApproval(assessmentCode, params = {}) {
    return async (dispatch) => {
        try {
            const response = await appealDetailsApprovalAPI(assessmentCode, params);
            return response;
        } catch (err) {
            throw err;
        }
    };
}

export const { clearAssessmentDetail } = slice.actions;
